import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Eyebrow } from "@/components/Eyebrow";
import { Scan, Sparkles, ImageIcon, CalendarClock, ArrowRight, ArrowLeft, Rocket } from "lucide-react";

const STEPS = [
  {
    eyebrow: "Welcome",
    title: "Your brand. AI-powered creative. One click.",
    body: "HDG Creative Studio turns any website into a complete, on-brand social content pipeline — from brand analysis to scheduled posts.",
    icon: Rocket,
  },
  {
    eyebrow: "How it works",
    title: "Four steps, one workflow",
    flow: [
      { icon: Scan, label: "Analyze", text: "Paste a URL — we extract colors, fonts, voice & audience." },
      { icon: Sparkles, label: "Ideate", text: "Generate on-brand concepts & captions with AI." },
      { icon: ImageIcon, label: "Render", text: "Turn concepts into polished visual assets & carousels." },
      { icon: CalendarClock, label: "Schedule", text: "Queue finished posts to your social calendar." },
    ],
  },
  {
    eyebrow: "Good to know",
    title: "Your library grows with you",
    body: "Every brand and asset is saved automatically — reach them anytime from the top bar. Keep an eye on your monthly usage with the plan badge, and tweak everything in Settings.",
    icon: Sparkles,
  },
];

export function OnboardingWizard({ open, onClose }) {
  const [step, setStep] = useState(0);
  useEffect(() => { if (open) setStep(0); }, [open]);

  const cur = STEPS[step];
  const last = step === STEPS.length - 1;
  const Icon = cur.icon;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent style={{ background: "var(--panel)", border: "1px solid var(--cardhover)", maxWidth: 480 }} data-testid="onboarding-wizard">
        <DialogTitle className="sr-only">Welcome to HDG Creative Studio</DialogTitle>
        <DialogDescription className="sr-only">A quick walkthrough of how the studio works.</DialogDescription>
        <Eyebrow>{cur.eyebrow}</Eyebrow>

        <div style={{ minHeight: 210, marginTop: 18 }}>
          {Icon && (
            <div style={{ width: 52, height: 52, borderRadius: 12, background: "rgba(24, 24, 27,0.12)", color: "var(--orange)", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 16 }}>
              <Icon size={26} />
            </div>
          )}
          <h2 className="font-display" style={{ fontSize: 22, fontWeight: 700, color: "var(--tprimary)", margin: 0, letterSpacing: "-0.02em", lineHeight: 1.2 }}>
            {cur.title}
          </h2>
          {cur.body && (
            <p className="font-body" style={{ fontSize: 14, color: "var(--tsecondary)", lineHeight: 1.6, marginTop: 12 }}>{cur.body}</p>
          )}
          {cur.flow && (
            <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 16 }}>
              {cur.flow.map((f, i) => {
                const FIcon = f.icon;
                return (
                  <div key={f.label} style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                    <div style={{ width: 34, height: 34, borderRadius: 8, background: "rgba(24, 24, 27,0.10)", color: "var(--orange)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <FIcon size={16} />
                    </div>
                    <div>
                      <span className="font-display" style={{ fontSize: 13.5, fontWeight: 600, color: "var(--tprimary)" }}>
                        <span className="font-mono" style={{ color: "var(--orange)", marginRight: 6 }}>0{i + 1}</span>{f.label}
                      </span>
                      <div className="font-body" style={{ fontSize: 12.5, color: "var(--tsecondary)", lineHeight: 1.5 }}>{f.text}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 14 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            {!last && (
              <button data-testid="onboarding-skip" onClick={onClose} className="font-mono" style={{ background: "transparent", border: "none", color: "var(--tmuted)", cursor: "pointer", fontSize: 11 }}>
                Skip
              </button>
            )}
            <div style={{ display: "flex", gap: 6 }}>
              {STEPS.map((_, i) => (
                <span key={i} style={{ width: i === step ? 18 : 6, height: 6, borderRadius: 999, background: i === step ? "var(--orange)" : "var(--cardhover)", transition: "width 0.25s ease" }} />
              ))}
            </div>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            {step > 0 && (
              <button data-testid="onboarding-back" className="btn-secondary" style={{ padding: "8px 14px" }} onClick={() => setStep((s) => s - 1)}>
                <ArrowLeft size={14} /> Back
              </button>
            )}
            {!last ? (
              <button data-testid="onboarding-next" className="btn-primary" style={{ padding: "8px 18px" }} onClick={() => setStep((s) => s + 1)}>
                Next <ArrowRight size={14} />
              </button>
            ) : (
              <button data-testid="onboarding-finish" className="btn-primary" style={{ padding: "8px 18px" }} onClick={onClose}>
                Start creating <ArrowRight size={14} />
              </button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
